import React from 'react'
import Tooltip from '@material-ui/core/Tooltip'
import Menu from '@material-ui/core/Menu'
import IconButton from '@material-ui/core/IconButton'
import { createStyles, Theme, makeStyles } from '@material-ui/core/styles'
import HelpIcon from '@material-ui/icons/Help'
import Fade from '@material-ui/core/Fade'
import { CardHeader } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import Grid from '@material-ui/core/Grid'
import { isMobile } from 'react-device-detect'
import SuggestionsMenuList from '../SuggestionsMenuList'

/* HTMLElement attributes passed as string values, so component logic must handle booleans as strings */
interface SuggestionsMenuProps {
  items: string[]
  helperText?: string
  inner?: boolean
  inline?: boolean
  subHeader?: string
  askHandler: (utterance: string) => void
  visible?: boolean
  askTimeout?: number
  listOnly?: boolean
  theme?: Theme
  areRowsContained?: boolean
}

const useMenuStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      flexWrap: 'wrap'
    },
    buttonInner: {
      margin: theme.spacing(1),
      marginRight: 0
    },
    buttonContainer: {
      display: 'inline',
      lineHeight: 'inherit',
      float: 'right'
    },
    buttonContainerInline: {
      float: 'none'
    },
    buttonOuter: {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1) / 2
    },
    helpIcon: {}
  })
)

const SuggestionsMenu: React.FC<SuggestionsMenuProps> = ({
  items,
  helperText = 'Find out more about this',
  inner = true,
  inline = true,
  subHeader,
  askHandler,
  visible = true,
  askTimeout,
  listOnly = false,
  theme,
  areRowsContained
}) => {
  const materialClasses = useMenuStyles()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <React.Fragment>
      {listOnly ? (
        <SuggestionsMenuList
          items={items}
          handleClose={handleClose}
          askHandler={askHandler}
          askTimeout={askTimeout}
          areRowsContained={areRowsContained}
        />
      ) : (
        <React.Fragment>
          {visible ? (
            <Grid
              item
              className={`${materialClasses.buttonContainer} ${
                inline ? materialClasses.buttonContainerInline : ''
              }`}
              component="span"
            >
              <Tooltip title={helperText} placement="right-end">
                <IconButton
                  aria-controls="help-menu"
                  aria-haspopup="true"
                  aria-label="Help icon"
                  className={
                    inner
                      ? materialClasses.buttonInner
                      : materialClasses.buttonOuter
                  }
                  color="secondary"
                  size="small"
                  onClick={handleClick}
                >
                  <HelpIcon fontSize="medium" />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                id="help-menu"
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                TransitionComponent={Fade}
                anchorOrigin={
                  anchorEl
                    ? undefined
                    : { vertical: 'bottom', horizontal: 'right' }
                }
                anchorPosition={{ left: 0, top: 0 }}
                anchorReference={isMobile ? 'anchorPosition' : 'anchorEl'}
                PaperProps={{
                  style: {
                    width: '40ch'
                  }
                }}
                MenuListProps={{
                  disablePadding: true,
                  subheader: (
                    <CardHeader
                      title={helperText}
                      titleTypographyProps={{ variant: 'body1' }}
                      subheader={subHeader}
                      action={
                        <IconButton
                          size="small"
                          onClick={handleClose}
                          aria-label="close"
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    />
                  )
                }}
              >
                <SuggestionsMenuList
                  items={items}
                  handleClose={handleClose}
                  askHandler={askHandler}
                  askTimeout={askTimeout}
                />
              </Menu>
            </Grid>
          ) : (
            ''
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  )
}

export default SuggestionsMenu
